import { useEffect, useState } from 'react';
import { Bank } from '../types/Bank';
import { getBanks } from '../api/banks';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries. Mirrors useFundTypes.
let cache: Bank[] | null = null;
let inflight: Promise<Bank[]> | null = null;

function loadBanks(): Promise<Bank[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getBanks()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

interface UseBanksResult {
  data: Bank[] | null;
  loading: boolean;
  error: string | null;
}

export function useBanks(): UseBanksResult {
  const [state, setState] = useState<UseBanksResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadBanks()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
